"use client";

import * as React from "react";
import { lookupAchievement } from "@modernatc/db";
import { cn } from "@modernatc/ui/cn";

interface ToastItem {
  id: string;
  achievementId: string;
  xp: number;
}

/**
 * Minimal in-app toaster for achievement unlocks.
 *
 * Listens for a custom `gamification:unlock` window event. Action
 * handlers (gradeCard, submitExam, etc.) return newly-unlocked ids in
 * their result; the calling component dispatches the event after
 * mounting the result. Keeps the toaster decoupled from action shape.
 *
 * No portal, no provider — single mount in (app)/layout.tsx is
 * sufficient. The component owns its own stack state.
 *
 * Auto-dismisses after 6s. Click to dismiss immediately.
 */
export function AchievementToaster() {
  const [items, setItems] = React.useState<ToastItem[]>([]);

  React.useEffect(() => {
    function onUnlock(ev: Event) {
      const detail = (ev as CustomEvent<{ ids: string[] }>).detail;
      if (!detail || !Array.isArray(detail.ids) || detail.ids.length === 0) {
        return;
      }
      setItems((prev) => {
        const next = [...prev];
        for (const id of detail.ids) {
          const ach = lookupAchievement(id);
          if (!ach) continue;
          // Stable id keeps React reconciliation tight when multiple
          // toasts pop in the same tick.
          next.push({
            id: `${id}:${Date.now()}:${Math.random().toString(36).slice(2, 6)}`,
            achievementId: id,
            xp: ach.xp,
          });
        }
        return next.slice(-5); // cap visible stack
      });
    }
    window.addEventListener("gamification:unlock", onUnlock);
    return () => window.removeEventListener("gamification:unlock", onUnlock);
  }, []);

  React.useEffect(() => {
    if (items.length === 0) return;
    const timers = items.map((it) =>
      setTimeout(() => {
        setItems((prev) => prev.filter((p) => p.id !== it.id));
      }, 6000),
    );
    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, [items]);

  if (items.length === 0) return null;
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed bottom-6 right-6 z-50 flex w-full max-w-sm flex-col gap-2"
    >
      {items.map((it) => {
        const ach = lookupAchievement(it.achievementId);
        if (!ach) return null;
        return (
          <button
            key={it.id}
            type="button"
            onClick={() =>
              setItems((prev) => prev.filter((p) => p.id !== it.id))
            }
            className={cn(
              "pointer-events-auto flex items-start gap-3 rounded-[var(--radius-md)] border border-[var(--color-amber)]/60 bg-[var(--color-panel)] p-4 text-left shadow-[0_8px_24px_rgba(21,33,46,0.12)] transition hover:border-[var(--color-amber)] focus:outline-none focus:ring-2 focus:ring-[var(--color-amber)]",
            )}
          >
            <span className="text-2xl" aria-hidden>
              {ach.icon}
            </span>
            <div className="min-w-0">
              <p className="text-callsign text-[10px] uppercase tracking-wide text-[var(--color-amber)]">
                Achievement unlocked · +{it.xp} XP
              </p>
              <p className="mt-1 text-sm font-medium text-[var(--color-ink)]">
                {ach.name}
              </p>
              <p className="mt-1 text-xs text-[var(--color-ink-dim)]">
                {ach.description}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Helper that action callers can use to fire the toast event from a
 * post-action React handler. Saves callers from re-typing the event
 * name + detail shape.
 */
export function emitAchievementUnlock(ids: string[] | undefined): void {
  if (!ids || ids.length === 0) return;
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("gamification:unlock", { detail: { ids } }),
  );
}
